import React from 'react';
import * as R from 'ramda';
import * as Yup from 'yup';
import { withFormik, FieldArray } from 'formik';
import { pure, compose, withProps, withHandlers } from 'react-recompose';
// components
import { FormFooter } from '../../../components/form-footer';
// forms
import { Fieldset2 } from '../../formik/fieldset2/fieldset';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import { ACCESSORIAL_TYPE_OPTIONS, DEFAULT_ACCESSORIAL_FORM_RATE_UNIT_GROUP_OPTIONS } from '../../../helpers/options';
// icons
import * as I from '../../../svgs';
// ui
import { Box, Flex } from '../../../ui';
import { scrollableContainerCss6px } from '../../../ui/common';
// feature shared-accessorials/
import MatrixVariablesSection from './matrix-variables';
import GeoFencingZoneForm from './geo-fencing-zone-form';
import FuelIndexVariablesSection from './fuel-index-variables';
import {
  addressPoint,
  accessorialFieldSettings,
  defaultMatrixVariableFields,
  defaultFuelIndexVariableFields,
  defaultAccessorialFieldSettings,
  getAccessorialValidationSchemaObject,
} from './field-settings';
//////////////////////////////////////////////////

const enhance = compose(
  withFormik({
    enableReinitialize: true,
    mapPropsToValues: ({ initialValues, showAutoApply, fromSharedAccessorialList }) => G.setInitialFormikValues(
      {
        ...defaultAccessorialFieldSettings,
        ...showAutoApply ? { [GC.FIELD_AUTO_APPLY]: true } : {},
        ...fromSharedAccessorialList ? { [GC.FIELD_TYPES]: [] } : {},
      },
      initialValues,
    ),
    validationSchema: ({ fromSharedAccessorialList }) => Yup.lazy(({ useFuelIndex, matrixType }) => (
      Yup.object().shape(getAccessorialValidationSchemaObject(
        useFuelIndex,
        matrixType,
        fromSharedAccessorialList,
      ))
    )),
    handleSubmit: (values, { props }) => {
      const { submitAction, fromSharedAccessorialList } = props;

      let valuesToSave = R.filter(G.isNotNilAndNotEmpty, values);

      if (fromSharedAccessorialList) {
        valuesToSave = R.assoc(GC.FIELD_TYPES, values[GC.FIELD_TYPES] || [], valuesToSave);
      }

      const { transportationMode } = values;

      if (G.isNotNilAndNotEmpty(transportationMode)) {
        valuesToSave = R.assoc(GC.FIELD_MODE, { [GC.FIELD_DROPDOWN_OPTION_GUID]: transportationMode }, valuesToSave);
      }

      submitAction(valuesToSave);
    },
  }),
  withHandlers({
    handleChangeUseFuelIndex: ({ values, setValues }) => () => {
      const { useFuelIndex } = values;

      if (useFuelIndex) {
        setValues(R.mergeRight(
          values,
          {
            [GC.FIELD_ACCESSORIAL_USE_FUEL_INDEX]: false,
            [GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES]: [],
          },
        ));
      } else {
        setValues(R.mergeRight(
          values,
          {
            [GC.FIELD_CHARGE_RATE]: '',
            [GC.FIELD_ACCESSORIAL_MATRIX_UOM]: null,
            [GC.FIELD_ACCESSORIAL_MATRIX_TYPE]: null,
            [GC.FIELD_ACCESSORIAL_USE_FUEL_INDEX]: true,
            [GC.FIELD_ACCESSORIAL_MATRIX_VARIABLES]: [],
            [GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES]: R.of(Array, defaultFuelIndexVariableFields()),
          },
        ));
      }
    },
    handleChangeMatrixType: ({ values, setValues }) => (event) => {
      const matrixType = R.path(['currentTarget', GC.FIELD_VALUE], event);
      const currentMatrixType = R.prop(GC.FIELD_ACCESSORIAL_MATRIX_TYPE, values);
      const useMatrix = G.isNotNilAndNotEmpty(matrixType);
      const matrixVariables = R.propOr([], GC.FIELD_ACCESSORIAL_MATRIX_VARIABLES, values);

      setValues(R.mergeRight(
        values,
        {
          [GC.FIELD_ACCESSORIAL_USE_FUEL_INDEX]: false,
          [GC.FIELD_ACCESSORIAL_MATRIX_TYPE]: matrixType,
          [GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES]: [],
          [GC.FIELD_CHARGE_RATE]: G.ifElse(useMatrix, '', R.prop(GC.FIELD_CHARGE_RATE, values)),
          [GC.FIELD_ACCESSORIAL_MATRIX_UOM]: G.ifElse(
            R.equals(matrixType, currentMatrixType),
            R.prop(GC.FIELD_ACCESSORIAL_MATRIX_UOM, values),
            null,
          ),
          [GC.FIELD_ACCESSORIAL_MATRIX_VARIABLES]: G.ifElse(
            useMatrix,
            G.ifElse(
              G.isNotNilAndNotEmpty(matrixVariables),
              matrixVariables,
              R.of(Array, defaultMatrixVariableFields()),
            ),
            [],
          ),
        },
      ));
    },
    handleChangeRateType: (props) => (event) => {
      const { values, setValues } = props;

      const rateType = R.path(['currentTarget', GC.FIELD_VALUE], event);
      let newValues = R.mergeRight(
        values,
        {
          rateType,
          [GC.FIELD_MAX_STOPS]: '',
          [GC.FIELD_MIN_STOPS]: '',
        },
      );

      if (R.equals(rateType, GC.CHARGE_RATE_TYPE_FLAT)) {
        newValues = R.mergeRight(
          newValues,
          {
            [GC.FIELD_CHARGE_MIN_RATE]: '',
            [GC.FIELD_CHARGE_MAX_RATE]: '',
          },
        );
      } else {
        newValues = R.mergeRight(
          newValues,
          {
            [GC.FIELD_ACCESSORIAL_MATRIX_UOM]: null,
            [GC.FIELD_ACCESSORIAL_MATRIX_TYPE]: null,
            [GC.FIELD_ACCESSORIAL_MATRIX_VARIABLES]: [],
          },
        );
      }

      setValues(newValues);
    },
    handleChangeAccessorial: (props) => (fieldValue) => {
      const { values, setValues, accessorialConfigList, fromSharedAccessorialList } = props;

      if (G.isNilOrEmpty(fieldValue)) {
        return setValues(R.mergeRight(
          values,
          {
            [GC.FIELD_MAX_STOPS]: '',
            [GC.FIELD_MIN_STOPS]: '',
            [GC.FIELD_DEDUCTION]: false,
            [GC.FIELD_NON_TAXABLE]: false,
            [GC.FIELD_FUEL_RELATED]: false,
            [GC.FIELD_ACCESSORIAL_MATRIX_UOM]: null,
            [GC.FIELD_ACCESSORIAL_MATRIX_TYPE]: null,
            [GC.FIELD_ACCESSORIAL_MATRIX_VARIABLES]: [],
            [GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID]: null,
          },
        ));
      }

      let selectedAccessorialConfig = R.compose(
        R.mergeRight({
          [GC.FIELD_MAX_STOPS]: '',
          [GC.FIELD_MIN_STOPS]: '',
          [GC.FIELD_ACCESSORIAL_MATRIX_UOM]: null,
          [GC.FIELD_ACCESSORIAL_MATRIX_TYPE]: null,
          [GC.FIELD_ACCESSORIAL_MATRIX_VARIABLES]: [],
        }),
        R.omit([GC.FIELD_DISPLAYED_VALUE, GC.FIELD_ORIGINAL_CONFIG_GUID]),
        R.prop(fieldValue),
      )(accessorialConfigList);

      if (R.propEq(GC.CHARGE_RATE_TYPE_FLAT, GC.FIELD_CHARGE_RATE_TYPE, selectedAccessorialConfig)) {
        selectedAccessorialConfig = R.mergeRight(
          selectedAccessorialConfig,
          {
            [GC.FIELD_CHARGE_MIN_RATE]: '',
            [GC.FIELD_CHARGE_MAX_RATE]: '',
          },
        );
      }

      if (fromSharedAccessorialList) {
        const types = selectedAccessorialConfig[GC.FIELD_TYPES] || [];

        selectedAccessorialConfig = R.assoc(GC.FIELD_TYPES, [...types], selectedAccessorialConfig);
      }

      setValues(R.mergeRight(values, selectedAccessorialConfig));
    },
    handleOpenGeoFencingZone: (props) => () => {
      const { values, setValues, openModal, closeModal, setFieldValue } = props;

      const zoneFilter = R.compose(
        G.isNotNilAndNotEmpty,
        R.filter(G.isNotNilAndNotEmpty),
        R.pick(R.keys(addressPoint)),
      );
      const submitAction = ({ origin, destination }) => {
        const newValues = R.mergeRight(
          values,
          {
            [GC.FIELD_ORIGIN]: G.ifElse(zoneFilter(origin), origin, null),
            useGeoFencingZone: R.or(zoneFilter(origin), zoneFilter(destination)),
            [GC.FIELD_DESTINATION]: G.ifElse(zoneFilter(destination), destination, null),
          },
        );

        closeModal();
        setValues(newValues);
      };
      const cancelAction = ({ origin, destination }) => {
        closeModal();
        setFieldValue('useGeoFencingZone', R.or(zoneFilter(origin), zoneFilter(destination)));
      };
      const initialValues = R.compose(
        R.filter(G.isNotNilAndNotEmpty),
        R.pick([GC.FIELD_ORIGIN, GC.FIELD_DESTINATION]),
      )(values);
      const component = (
        <GeoFencingZoneForm
          cancelAction={cancelAction}
          submitAction={submitAction}
          initialValues={initialValues}
        />
      );
      const options = {
        p: 15,
        component,
        options: {
          title: G.getAddTitle(['titles:address-points', 'Address Points']),
        },
      };

      openModal(options);
    },
  }),
  withProps(({ values, accessorialConfigList, accessorialConfigOptions, fromSharedAccessorialList }) => {
    if (!fromSharedAccessorialList) return {};

    const selectedTypes = values[GC.FIELD_TYPES] || [];
    const selectedGuid = values[GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID];
    const allowedTypes = R.path([selectedGuid, GC.FIELD_TYPES], accessorialConfigList);

    return {
      accessorialTypeOptions: ACCESSORIAL_TYPE_OPTIONS.filter(({ value }) => (
        G.isNilOrEmpty(allowedTypes) || allowedTypes.includes(value)
      )),
      accessorialConfigOptions: (accessorialConfigOptions || []).filter(({ value }) => {
        const types = R.path([value, GC.FIELD_TYPES], accessorialConfigList);

        return G.isNilOrEmpty(types) || selectedTypes.every((type) => types.includes(type));
      }),
    };
  }),
  pure,
);

const getRateSectionFields = (props) => {
  const { entity } = props;

  const { rateSection, carrierRateSection } = accessorialFieldSettings;

  if (R.equals(entity, 'carrier')) return carrierRateSection;

  return rateSection;
};

const AccessorialForm = (props) => (
  <form onSubmit={props.handleSubmit}>
    <Box overflow='auto' maxHeight='calc(100vh - 160px)' css={scrollableContainerCss6px}>
      <Fieldset2
        {...G.getFormikProps(props)}
        handleCustomChange={props.handleChangeAccessorial}
        fieldsWrapperStyles={{ mt: 15, mb: 25, width: 980 }}
        accessorialTypeOptions={props.accessorialTypeOptions}
        accessorialConfigOptions={props.accessorialConfigOptions}
        transportationModeOptions={props.transportationModeOptions}
        justifyContent={G.ifElse(R.propEq('fromSharedAccessorialList', true, props), 'space-between', null)}
        fields={accessorialFieldSettings.accessorialSection(
          R.pick(['isVendor', 'isDriver', 'showAutoApply', 'fromSharedAccessorialList'], props),
        )}
      />
      <Fieldset2
        {...G.getFormikProps(props)}
        fields={getRateSectionFields(props)}
        fieldsWrapperStyles={{ width: 980 }}
        handleCustomChange={props.handleChangeRateType}
        rateUnit={R.pathOr(
          R.of(Array, GC.EMPTY_OPTION_OBJECT),
          [R.path(['values', GC.FIELD_CHARGE_RATE_TYPE], props)],
          DEFAULT_ACCESSORIAL_FORM_RATE_UNIT_GROUP_OPTIONS,
        )}
      />
      <Flex>
        <Fieldset2
          {...G.getFormikProps(props)}
          flexGrow='unset'
          handleCustomChange={props.handleChangeUseFuelIndex}
          handleOpenGeoFencingZone={props.handleOpenGeoFencingZone}
          fields={accessorialFieldSettings.districtSection(
            props.fromSharedAccessorialList,
            R.path(['values', GC.FIELD_ACCESSORIAL_USE_FUEL_INDEX], props),
            R.prop('fromSharedAccessorialList', props),
          )}
        />
        {
          R.path(['values', 'useGeoFencingZone'], props) &&
          <Flex ml={20} mb={25} cursor='pointer' onClick={props.handleOpenGeoFencingZone}>
            <Box fontWeight='bold' color={G.getTheme('colors.light.blue')}>
              {G.getWindowLocale('titles:edit-geo-fencing-zone', 'Edit Geo Fencing Zone')}
            </Box>
            <Box ml={10}>
              {I.pencil()}
            </Box>
          </Flex>
        }
      </Flex>
      {
        G.isAllTrue(
          R.pathEq(GC.CHARGE_RATE_TYPE_FLAT, ['values', GC.FIELD_CHARGE_RATE_TYPE], props),
          G.isFalse(R.path(['values', GC.FIELD_ACCESSORIAL_USE_FUEL_INDEX], props)),
        ) &&
        <Fieldset2
          {...G.getFormikProps(props)}
          flexGrow='unset'
          handleCustomChange={props.handleChangeMatrixType}
          fields={accessorialFieldSettings.matrixSection(R.path(['values', GC.FIELD_ACCESSORIAL_MATRIX_TYPE], props))}
        />
      }
      {
        R.path(['values', GC.FIELD_ACCESSORIAL_USE_FUEL_INDEX], props) &&
        <FieldArray
          name={GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES}
          render={(arrayHelpers) => (
            <FuelIndexVariablesSection
              {...arrayHelpers}
              openLoader={props.openLoader}
              closeLoader={props.closeLoader}
              importFuelIndexVariables={props.importFuelIndexVariables}
              handleImportFuelIndexVariables={props.handleImportFuelIndexVariables}
            />
          )}
        />
      }
      {
        G.isAllTrue(
          R.pathEq(GC.CHARGE_RATE_TYPE_FLAT, ['values', GC.FIELD_CHARGE_RATE_TYPE], props),
          G.isFalse(R.path(['values', GC.FIELD_ACCESSORIAL_USE_FUEL_INDEX], props)),
          G.isNotNilAndNotEmpty(R.path(['values', GC.FIELD_ACCESSORIAL_MATRIX_TYPE], props)),
        ) &&
        <FieldArray
          name={GC.FIELD_ACCESSORIAL_MATRIX_VARIABLES}
          render={(arrayHelpers) => (
            <MatrixVariablesSection {...arrayHelpers} />
          )}
        />
      }
    </Box>
    <FormFooter
      submitBtnStyles={{
        display: G.ifElse(R.path(['values', 'shared'], props), 'none'),
      }}
    />
  </form>
);

export const DefaultAccessorialForm = enhance((props) => {
  const {
    isOrder,
    handleSubmit,
    handleChangeRateType,
    handleChangeAccessorial,
    accessorialConfigOptions,
  } = props;

  return (
    <form onSubmit={handleSubmit}>
      <Fieldset2
        {...G.getFormikProps(props)}
        handleCustomChange={handleChangeAccessorial}
        accessorialConfigOptions={accessorialConfigOptions}
        fieldsWrapperStyles={{ mt: 15, mb: 25, width: 980 }}
        fields={accessorialFieldSettings.accessorialSection()}
        handlers={{ handleCustomChange: handleChangeAccessorial }}
      />
      <Fieldset2
        {...G.getFormikProps(props)}
        fieldsWrapperStyles={{ width: 980 }}
        handleCustomChange={handleChangeRateType}
        fields={G.getPropFromObject(
          G.ifElse(isOrder, 'defaultOrderAccessorialSection', 'defaultTripAccessorialSection'),
          accessorialFieldSettings,
        )}
        rateUnit={R.pathOr(
          R.of(Array, GC.EMPTY_OPTION_OBJECT),
          [R.path(['values', GC.FIELD_CHARGE_RATE_TYPE], props)],
          DEFAULT_ACCESSORIAL_FORM_RATE_UNIT_GROUP_OPTIONS,
        )}
      />
      <FormFooter />
    </form>
  );
});

export default enhance(AccessorialForm);
